import {GetServerSidePropsContext} from "next";
import {ReactElement, ReactNode, useMemo} from "react";
import type {NextPageWithLayout} from "@pages/_app";
import {Layout} from "@components/layouts/layout";
import {Plugin} from "@hooks/plugins/plugin";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import {IPluginsDataResponse} from "@hooks/plugins/IPluginsDataResponse";
import Link from "@components/elements/DocumentLink";
import {StarRating} from "@components/elements/StarRating";
import {getPluginMaintenanceInsights} from "@utils/pluginInsights";
import {PluginsGetter} from "../../server/api/data/PluginsGetter";
import {getDb} from "../../server/api/db";
import {twMerge} from "tailwind-merge";
import {runAfterResponse} from "@utils/afterResponse";
import {formatNumber} from "@utils/format";
import {useStableNow} from "@hooks/useStableNow";
import {CACHE_WARM_HEADERS, shouldWarmCache} from "@utils/cacheWarm";

const parseComparisonSlug = (slug: string): {a: string; b: string} | null => {
    const normalized = slug.trim().toLowerCase();
    const separator = "-vs-";
    const separatorIndex = normalized.indexOf(separator);
    if (separatorIndex <= 0) return null;
    const a = normalized.slice(0, separatorIndex).trim();
    const b = normalized.slice(separatorIndex + separator.length).trim();
    if (!a || !b) return null;
    if (a === b) return null;
    return {a, b};
};

const daysSince = (date: Date, nowMs?: number | null): number | null => {
    if (!nowMs) return null;
    const diffMs = nowMs - date.getTime();
    if (!Number.isFinite(diffMs)) return null;
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    context.res.setHeader(
        'Cache-Control',
        'public, s-maxage=3600, stale-while-revalidate=86400'
    );

    const renderedAt = Date.now();
    const slug = String(context.params?.slug ?? "");
    const parsed = parseComparisonSlug(slug);
    if (!parsed) {
        return {notFound: true};
    }

    const db = getDb();
    const getter = new PluginsGetter();
    const [aResponse, bResponse] = await Promise.all([
        getter.get({db, pluginid: parsed.a, page: 1, per_page: 1}) as Promise<IPluginsDataResponse>,
        getter.get({db, pluginid: parsed.b, page: 1, per_page: 1}) as Promise<IPluginsDataResponse>,
    ]);

    const pluginA = aResponse.data?.plugins?.[0];
    const pluginB = bResponse.data?.plugins?.[0];

    if (!pluginA || !pluginB) {
        return {notFound: true};
    }

    // Warm CDN cache for individual plugin pages after response is sent
    const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;
    if (baseUrl && shouldWarmCache(context.req)) {
        runAfterResponse(context.res, async () => {
            const urlsToWarm = [
                `${baseUrl}/plugins/${pluginA.pluginid}`,
                `${baseUrl}/plugins/${pluginB.pluginid}`,
                `${baseUrl}/alternatives-to/${pluginA.pluginid}`,
                `${baseUrl}/alternatives-to/${pluginB.pluginid}`,
            ];
            await Promise.allSettled(
                urlsToWarm.map(url =>
                    fetch(url, { method: 'GET', headers: CACHE_WARM_HEADERS }).catch(() => {})
                )
            );
        });
    }

    return {
        props: {
            slug: `${parsed.a}-vs-${parsed.b}`,
            pluginAId: pluginA.pluginid,
            pluginBId: pluginB.pluginid,
            pluginAName: pluginA.name,
            pluginBName: pluginB.name,
            stringifyPluginAData: JSON.stringify(pluginA),
            stringifyPluginBData: JSON.stringify(pluginB),
            renderedAt,
        },
    };
};

const ComparePage: NextPageWithLayout = (props: any) => {
    const now = useStableNow(props.renderedAt);
    const pluginA = useMemo<IProcessedPluginData | null>(() => {
        if (!props.stringifyPluginAData) return null;
        try {
            return Plugin.processApiResponse(JSON.parse(props.stringifyPluginAData), now ?? props.renderedAt);
        } catch {
            return null;
        }
    }, [props.stringifyPluginAData, now, props.renderedAt]);

    const pluginB = useMemo<IProcessedPluginData | null>(() => {
        if (!props.stringifyPluginBData) return null;
        try {
            return Plugin.processApiResponse(JSON.parse(props.stringifyPluginBData), now ?? props.renderedAt);
        } catch {
            return null;
        }
    }, [props.stringifyPluginBData, now, props.renderedAt]);

    if (!pluginA || !pluginB) {
        return (
            <div className="flex items-center justify-center h-full p-8">
                <div className="text-center text-gray-600 dark:text-gray-300">Plugins not found.</div>
            </div>
        );
    }

    const maintenanceA = getPluginMaintenanceInsights(pluginA, now ?? props.renderedAt);
    const maintenanceB = getPluginMaintenanceInsights(pluginB, now ?? props.renderedAt);

    const supportResolvedPercent = (plugin: IProcessedPluginData): number | null => {
        if (!plugin.support_threads) return null;
        return Math.round((plugin.support_threads_resolved / plugin.support_threads) * 100);
    };

    const rows: {label: string; a: ReactNode; b: ReactNode}[] = [
        {
            label: "Rating",
            a: (
                <div className="flex flex-wrap items-center gap-2">
                    <StarRating rating={pluginA.rating} maxRating={5} starSizeClassName="w-4 h-4 sm:w-5 sm:h-5"/>
                    <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                        {pluginA.rating.toFixed(1)}/5 ({formatNumber(pluginA.num_ratings)})
                    </span>
                </div>
            ),
            b: (
                <div className="flex flex-wrap items-center gap-2">
                    <StarRating rating={pluginB.rating} maxRating={5} starSizeClassName="w-4 h-4 sm:w-5 sm:h-5"/>
                    <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                        {pluginB.rating.toFixed(1)}/5 ({formatNumber(pluginB.num_ratings)})
                    </span>
                </div>
            ),
        },
        {
            label: "Active installs",
            a: formatNumber(pluginA.active_installs),
            b: formatNumber(pluginB.active_installs),
        },
        {
            label: "Downloads",
            a: formatNumber(pluginA.downloaded ?? 0),
            b: formatNumber(pluginB.downloaded ?? 0),
        },
        {
            label: "Last updated",
            a: daysSince(pluginA.last_updated, now) === null ? "—" : `${daysSince(pluginA.last_updated, now)} days ago`,
            b: daysSince(pluginB.last_updated, now) === null ? "—" : `${daysSince(pluginB.last_updated, now)} days ago`,
        },
        {
            label: "Maintenance score",
            a: `${maintenanceA.score}/100`,
            b: `${maintenanceB.score}/100`,
        },
        {
            label: "Support resolved",
            a: supportResolvedPercent(pluginA) === null ? "—" : `${supportResolvedPercent(pluginA)}%`,
            b: supportResolvedPercent(pluginB) === null ? "—" : `${supportResolvedPercent(pluginB)}%`,
        },
        {
            label: "Languages",
            a: `${(pluginA.languages?.length ?? 0) + 1}`,
            b: `${(pluginB.languages?.length ?? 0) + 1}`,
        },
        {
            label: "Requires WordPress",
            a: pluginA.minimum_wordpress_version || "Unknown",
            b: pluginB.minimum_wordpress_version || "Unknown",
        },
        {
            label: "Tested up to",
            a: pluginA.last_wordpress_compatible_version || "Unknown",
            b: pluginB.last_wordpress_compatible_version || "Unknown",
        },
        {
            label: "Requires PHP",
            a: pluginA.minimum_php_version || "Unknown",
            b: pluginB.minimum_php_version || "Unknown",
        },
    ];

    return (
        <div className="flex flex-col gap-6 p-4 md:p-8 max-w-6xl mx-auto">
            <div className="flex flex-col gap-2">
                <h1 className="text-2xl md:text-4xl font-bold">
                    {pluginA.name} vs {pluginB.name}
                </h1>
                <p className="text-gray-700 dark:text-gray-300">
                    Compare reviews, downloads, installs, update activity, support responsiveness, maintenance score, and WP/PHP compatibility.
                </p>
                <div className="flex flex-row flex-wrap gap-4 text-sm">
                    <Link
                        href={`/plugins/${pluginA.pluginid}`}
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                    >
                        View {pluginA.name}
                    </Link>
                    <Link
                        href={`/plugins/${pluginB.pluginid}`}
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                    >
                        View {pluginB.name}
                    </Link>
                    <Link
                        href={`/alternatives-to/${pluginA.pluginid}`}
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                    >
                        Alternatives to {pluginA.name}
                    </Link>
                    <Link
                        href={`/alternatives-to/${pluginB.pluginid}`}
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                    >
                        Alternatives to {pluginB.name}
                    </Link>
                </div>
            </div>

            <div className={twMerge("border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden")}>
                <div className={twMerge("hidden sm:grid sm:grid-cols-3", "bg-gray-50 dark:bg-gray-800")}>
                    <div className="p-3 font-semibold">Metric</div>
                    <div className="p-3 font-semibold">{pluginA.name}</div>
                    <div className="p-3 font-semibold">{pluginB.name}</div>
                </div>
                {rows.map((row) => (
                    <div
                        key={row.label}
                        className={twMerge("grid grid-cols-1 gap-2 sm:gap-0 sm:grid-cols-3", "border-t border-gray-200 dark:border-gray-700")}
                    >
                        <div className="p-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                            {row.label}
                        </div>
                        <div className="p-3 text-xs sm:text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden">
                                {pluginA.name}
                            </div>
                            <div className="min-w-0">{row.a}</div>
                        </div>
                        <div className="p-3 text-xs sm:text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden">
                                {pluginB.name}
                            </div>
                            <div className="min-w-0">{row.b}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

ComparePage.getLayout = function getLayout(page: ReactElement, props: any) {
    const baseUrl = String(process.env.NEXT_PUBLIC_WEBSITE_URL || "");
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const canonicalUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/compare/${props.slug}` : `/compare/${props.slug}`;
    const imageUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/catspyjamas.jpg` : "/catspyjamas.jpg";

    return (
        <Layout
            title={`${props.pluginAName} vs ${props.pluginBName}`}
            description={`Compare ${props.pluginAName} vs ${props.pluginBName}: reviews, downloads, installs, maintenance score, support responsiveness, and WP/PHP compatibility.`}
            url={canonicalUrl}
            image={imageUrl}
        >
            {page}
        </Layout>
    );
};

export default ComparePage;
